/* eslint-disable object-shorthand */
import axios from 'axios/index'

import config from '../../config'
import action, { actionFailed } from '../../util/action'
import createAsyncConst from '../../util/createAsyncConst'
import { useSelector } from 'react-redux'

export const REMOVE_ATTENDANCE = createAsyncConst('REMOVE_ATTENDANCE')
export const SUBMIT_ATTENDANCE = createAsyncConst('SUBMIT_ATTENDANCE')
export const FETCH_ATTENDANCE_INIT = createAsyncConst('FETCH_ATTENDANCE_INIT')
export const FIND_ATTENDANCE_CLIENT = createAsyncConst('FIND_ATTENDANCE_CLIENT')
export const EDIT_ATTENDANCE = createAsyncConst('EDIT_ATTENDANCE')
export const SEND_MAIL = createAsyncConst('SEND_MAIL')
export const GENERATE_PAYMENT = createAsyncConst('GENERATE_PAYMENT')
export const ROLLBACK_ITEM_STATUS = createAsyncConst('ROLLBACK_ITEM_STATUS')
export const FETCH_HISTORIC = createAsyncConst('FETCH_HISTORIC')
export const FETCH_ITEM_HISTORIC = createAsyncConst('FETCH_ITEM_HISTORIC')
export const FETCH_ITEM_BY_BARCODE = createAsyncConst('FETCH_ITEM_BY_BARCODE')
export const ARCHIVE = createAsyncConst('ARCHIVE')
export const FETCH_ATTENDANCE_COMMENTS = createAsyncConst(
  'FETCH_ATTENDANCE_COMMENTS'
)
export const FETCH_NFSE_COMPANIES = createAsyncConst('FETCH_NFSE_COMPANIES')
export const DOWNLOAD_IMAGES_FROM_ITEM = createAsyncConst(
  'DOWNLOAD_IMAGES_FROM_ITEM'
)
export const DONWLOAD_ORCAMENTO = createAsyncConst('DONWLOAD_ORCAMENTO')
export const FETCH_PAYMENT_INFORMATION = createAsyncConst(
  'FETCH_PAYMENT_INFORMATION'
)
export const FETCH_COMMISSIONED_PERSONS = createAsyncConst(
  'FETCH_COMMISSIONED_PERSONS'
)
export const FETCH_ATTENDANCE_HISTORY = createAsyncConst(
  'FETCH_ATTENDANCE_HISTORY'
)
export const CHANGE_RESPONSIBLE = createAsyncConst('CHANGE_RESPONSIBLE')
export const FETCH_DOCUMENT_CONTEXTS = createAsyncConst(
  'FETCH_DOCUMENT_CONTEXTS'
)
export const SYNC_SERVICE_IMAGES = createAsyncConst('SYNC_SERVICE_IMAGES')

export function fetchAttendanceInit(filters = {}, rows, page) {
  return dispatch => {
    dispatch(action(FETCH_ATTENDANCE_INIT.ACTION))
    return axios
      .get(`${config.API_URL}/v2/attendance`, {
        params: {
          filters: JSON.stringify(filters),
          rows,
          page: page || 1,
        },
      })
      .then(resp => {
        dispatch(
          action(FETCH_ATTENDANCE_INIT.SUCCESS, {
            data: resp.data,
            filters: filters,
          })
        )
        return resp
      })
      .catch(e => dispatch(actionFailed(FETCH_ATTENDANCE_INIT, e)))
  }
}

export function fetchCommissionedPersons() {
  return dispatch => {
    dispatch(action(FETCH_COMMISSIONED_PERSONS.ACTION))
    return axios
      .get(`${config.API_URL}/attendance/commissioned-persons`)
      .then(resp => {
        dispatch(action(FETCH_COMMISSIONED_PERSONS.SUCCESS, resp.data))
        return resp
      })
      .catch(e => dispatch(actionFailed(FETCH_COMMISSIONED_PERSONS, e)))
  }
}

export function fetchDocumentContexts() {
  return dispatch => {
    dispatch(action(FETCH_DOCUMENT_CONTEXTS.ACTION, {}, false, false))
    return axios
      .get(`${config.API_URL}/document-contexts`)
      .then(resp => {
        dispatch(
          action(FETCH_DOCUMENT_CONTEXTS.SUCCESS, resp.data, false, false)
        )
        return resp
      })
      .catch(e => {
        if (e?.response?.data) {
          return dispatch(actionFailed(FETCH_DOCUMENT_CONTEXTS, e, false))
        }

        return dispatch(
          action(
            FETCH_DOCUMENT_CONTEXTS.FAILURE,
            null,
            { message: 'Não foi possível carregar os contextos das fotos.' },
            false
          )
        )
      })
  }
}

/**
 * @author Heitor Santos 30/07/2026
 * Monta o payload multipart do sync-images a partir dos itens do form.
 *
 * O item é identificado pela POSIÇÃO em `items` (0-based), que o backend casa com
 * `$service->items` ordenado por `order, id` — a mesma ordem em que os itens foram
 * enviados no store/update. Só entram no payload os índices que mudaram; item
 * intocado é omitido (chaves esparsas preservam a posição dos demais).
 *
 * A sincronização é declarativa: toda foto existente que não vier em `paths` é
 * deletada (registro + arquivo no S3). Por isso a comparação é feita sobre o
 * `path` (nunca o `link`, que é temporário e muda a cada request).
 */
export const buildSyncImagesFormData = (services = []) => {
  const data = new FormData()
  let hasChanges = false

  services.forEach((service, index) => {
    const images = service?.images || []
    const documents = service?.documents || []

    const kept = images.filter(image => image?.kind === 'path')
    const files = images.filter(image => image?.kind === 'file' && image?.file)

    const contextChanged = kept.some(image => {
      const original = documents.find(document => document.path === image.path)

      return original && (original.context || '') !== (image.context || '')
    })

    const changed =
      files.length > 0 || kept.length !== documents.length || contextChanged

    if (!changed) return

    hasChanges = true

    // Objeto vazio não é representável em multipart: `clear` é como o backend
    // sabe que todas as fotos do item foram removidas.
    if (!kept.length && !files.length) {
      data.append(`items[${index}][clear]`, 1)
      return
    }

    kept.forEach((image, position) => {
      data.append(`items[${index}][paths][${position}][path]`, image.path)

      // Para foto mantida o `context` é opcional: omitido, o backend preserva o
      // que já está gravado (caso de foto antiga que veio sem contexto).
      if (image.context) {
        data.append(
          `items[${index}][paths][${position}][context]`,
          image.context
        )
      }
    })

    files.forEach((image, position) => {
      data.append(`items[${index}][files][${position}][file]`, image.file)
      data.append(`items[${index}][files][${position}][context]`, image.context)
    })
  })

  return hasChanges ? data : null
}

export function syncServiceImages(serviceId, services) {
  return async dispatch => {
    const payload = buildSyncImagesFormData(services)

    if (!serviceId || !payload) return true

    dispatch(action(SYNC_SERVICE_IMAGES.ACTION, {}, false, false))

    try {
      const resp = await axios.post(
        `${config.API_URL}/attendance/${serviceId}/sync-images`,
        payload
      )

      dispatch(action(SYNC_SERVICE_IMAGES.SUCCESS, resp?.data, false, false))

      return true
    } catch (e) {
      /**
       * Roda em segundo plano depois do save do atendimento: não pode ligar o
       * loading da tela nem rejeitar (abortaria a navegação de quem aguarda).
       * O erro chega ao usuário pelo snackbar global.
       */
      if (e?.response?.data) {
        dispatch(actionFailed(SYNC_SERVICE_IMAGES, e, false))
      } else {
        dispatch(
          action(
            SYNC_SERVICE_IMAGES.FAILURE,
            null,
            { message: 'Não foi possível sincronizar as imagens dos itens.' },
            false
          )
        )
      }

      return false
    }
  }
}

export function findClient(id) {
  return dispatch => {
    dispatch(action(FIND_ATTENDANCE_CLIENT.ACTION))
    return axios
      .get(`${config.API_URL}/clients/${id}`, {
        params: {
          showAll: true,
        },
      })
      .then(resp => resp.data)
      .then(resp => {
        dispatch(action(FIND_ATTENDANCE_CLIENT.SUCCESS, resp.data))
        return resp
      })
      .catch(e => dispatch(actionFailed(FIND_ATTENDANCE_CLIENT, e)))
  }
}

export function edit(id) {
  return dispatch => {
    dispatch(action(EDIT_ATTENDANCE.ACTION, { id }))
    return axios
      .get(`${config.API_URL}/attendance/${id}`)
      .then(resp => resp.data)
      .then(data => {
        dispatch(action(EDIT_ATTENDANCE.SUCCESS, data))
        return data
      })
      .catch(e => dispatch(actionFailed(EDIT_ATTENDANCE, e)))
  }
}

/**
 * `images` é estado só do form (o `File` não serializa em JSON) e vai para a API
 * pelo sync-images, depois do save. Não pertence ao payload do atendimento.
 */
const withoutItemImages = (values = {}) => ({
  ...values,
  services: (values?.services || []).map(({ images, ...service }) => service),
})

const submit = (values, method) => {
  const id = values.id ? `/${values.id}` : ''

  return async (dispatch, getState) => {
    dispatch(action(SUBMIT_ATTENDANCE.ACTION))

    try {
      const resp = await axios[method](
        `${config.API_URL}/attendance${id}`,
        withoutItemImages(values)
      )

      dispatch(action(SUBMIT_ATTENDANCE.SUCCESS, resp?.data))
      const rowsPerPage = getState().attendance.sizePerPage
      dispatch(fetchAttendanceInit(null, rowsPerPage))

      return resp?.data
    } catch (e) {
      dispatch(actionFailed(SUBMIT_ATTENDANCE, e))
    }
  }
}

export function post(values) {
  return submit(values, 'post')
}

export function update(values) {
  return submit(values, 'put')
}

export function remove(loss_reason, id) {
  return (dispatch, getState) => {
    dispatch(action(REMOVE_ATTENDANCE.ACTION))
    return axios
      .delete(`${config.API_URL}/attendance/${id}?loss_reason=${loss_reason}`)
      .then(resp => resp.data)
      .then(data => {
        dispatch(action(REMOVE_ATTENDANCE.SUCCESS, data))
        const rowsPerPage = getState().attendance.sizePerPage
        dispatch(fetchAttendanceInit(null, rowsPerPage))
        return true
      })
      .catch(e => {
        dispatch(actionFailed(REMOVE_ATTENDANCE, e))
      })
  }
}

export function sendMail(id) {
  return dispatch => {
    dispatch(action(SEND_MAIL.ACTION))
    return axios
      .get(`${config.API_URL}/send-mail/${id}`)
      .then(resp => resp.data)
      .then(data => {
        dispatch(action(SEND_MAIL.SUCCESS, data))
      })
      .catch(e => actionFailed(SEND_MAIL.FAILURE, e))
  }
}

export function generatePayment(id, values) {
  return (dispatch, getState) => {
    dispatch(action(GENERATE_PAYMENT.ACTION, { id }))

    return axios
      .post(`${config.API_URL}/billing/generate-billing-client/${id}`, values)
      .then(resp => resp.data)
      .then(data => {
        dispatch(action(GENERATE_PAYMENT.SUCCESS, data))
        const rowsPerPage = getState().attendance.sizePerPage
        dispatch(fetchAttendanceInit(null, rowsPerPage))
        return data
      })
      .catch(e => dispatch(actionFailed(GENERATE_PAYMENT, e)))
  }
}

export function saveFilters(filters) {
  return { type: 'SAVE_FILTERS', payload: filters }
}

export function eraseFilters() {
  return { type: 'ERASE_FILTERS' }
}

export function getHistory(id) {
  return dispatch => {
    dispatch(action(FETCH_HISTORIC.ACTION))
    return axios
      .get(`${config.API_URL}/client/history/${id}`)
      .then(resp => resp.data)
      .then(data => {
        dispatch(action(FETCH_HISTORIC.SUCCESS, data))
        return data
      })
      .catch(e => dispatch(actionFailed(FETCH_HISTORIC, e)))
  }
}

export function getItemHistory(id) {
  return dispatch => {
    dispatch(action(FETCH_ITEM_HISTORIC.ACTION))
    return axios
      .get(`${config.API_URL}/service-item/history/${id}`)
      .then(resp => resp.data)
      .then(data => {
        dispatch(action(FETCH_ITEM_HISTORIC.SUCCESS, data))
        return data
      })
      .catch(e => dispatch(actionFailed(FETCH_ITEM_HISTORIC, e)))
  }
}

export function getItemByBarcode(barCode) {
  return dispatch => {
    dispatch(action(FETCH_ITEM_BY_BARCODE.ACTION))
    return axios
      .get(`${config.API_URL}/service-item/barcode/${barCode}`)
      .then(resp => resp.data)
      .then(data => {
        dispatch(action(FETCH_ITEM_BY_BARCODE.SUCCESS, data))
        return data
      })
      .catch(e => dispatch(actionFailed(FETCH_ITEM_BY_BARCODE, e)))
  }
}

export function archive(form, id) {
  return dispatch => {
    dispatch(action(ARCHIVE.ACTION))
    return axios
      .put(`${config.API_URL}/attendance/${id}/archive`, form)
      .then(data => {
        dispatch(action(ARCHIVE.SUCCESS, data))
        return true
      })
      .catch(e => dispatch(actionFailed(ARCHIVE, e)))
  }
}

export function getAttendanceComments(id) {
  return dispatch => {
    dispatch(action(FETCH_ATTENDANCE_COMMENTS.ACTION))
    return axios
      .get(`${config.API_URL}/attendance/${id}/comments`)
      .then(resp => resp.data)
      .then(data => {
        dispatch(action(FETCH_ATTENDANCE_COMMENTS.SUCCESS, data))
        return data
      })
      .catch(e => dispatch(actionFailed(FETCH_ATTENDANCE_COMMENTS, e)))
  }
}

export function downloadImagesAsZip(id) {
  return dispatch => {
    dispatch(action(DOWNLOAD_IMAGES_FROM_ITEM.ACTION))
    return axios
      .get(`${config.API_URL}/service-item/${id}/download-images-as-zip`, {
        responseType: 'blob',
        validateStatus: status => status <= 500,
      })
      .then(async response => {
        if (response.status !== 200) {
          const error = JSON.parse(await response.data.text())
          dispatch(
            actionFailed(FETCH_ATTENDANCE_COMMENTS, {
              response: {
                data: error,
              },
            })
          )
        } else {
          dispatch(action(DOWNLOAD_IMAGES_FROM_ITEM.SUCCESS, {}))
          const blob = new Blob([response.data], { type: response.data.type })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          const contentDisposition = response.headers['content-disposition']
          let fileName = `images-${id}.zip`
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename=(.+)/)
            if (fileNameMatch.length === 2) {
              fileName = fileNameMatch[1].replaceAll('"', '')
            }
          }
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        }
      })
  }
}

export function downloadOrcamento(id, type) {
  return dispatch => {
    dispatch(action(DONWLOAD_ORCAMENTO.ACTION))
    return axios
      .get(`${config.API_URL}/download-orcamento/${id}/${type}`, {
        responseType: 'blob',
        validateStatus: status => status <= 500,
      })
      .then(async response => {
        if (response.status !== 200) {
          const error = JSON.parse(await response.data.text())
          dispatch(
            actionFailed(DONWLOAD_ORCAMENTO, {
              response: {
                data: error,
              },
            })
          )
        } else {
          dispatch(action(DONWLOAD_ORCAMENTO.SUCCESS, {}))
          const blob = new Blob([response.data], { type: response.data.type })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          const contentDisposition = response.headers['content-disposition']
          let fileName = `unknown.docx`
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename=(.+)/)
            if (fileNameMatch.length === 2) {
              fileName = fileNameMatch[1].replaceAll('"', '')
            }
          }
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        }
      })
  }
}

export function getPaymentInformation(id) {
  return dispatch => {
    dispatch(action(FETCH_PAYMENT_INFORMATION.ACTION))
    return axios
      .get(`${config.API_URL}/attendance/${id}/payment-information`)
      .then(resp => resp.data)
      .then(data => {
        dispatch(action(FETCH_PAYMENT_INFORMATION.SUCCESS, data))
        return data
      })
      .catch(e => dispatch(actionFailed(FETCH_PAYMENT_INFORMATION, e)))
  }
}

export function rollbackItemStatus(id, form) {
  return dispatch => {
    dispatch(action(ROLLBACK_ITEM_STATUS.ACTION))
    return axios
      .post(`${config.API_URL}/service-item/rollback-status-force/${id}`, form)
      .then(resp => resp.data)
      .then(data => {
        dispatch(action(ROLLBACK_ITEM_STATUS.SUCCESS))
        return data
      })
      .catch(e => dispatch(actionFailed(ROLLBACK_ITEM_STATUS, e)))
  }
}

export function getAttendanceHistory(id) {
  return dispatch => {
    dispatch(action(FETCH_ATTENDANCE_HISTORY.ACTION))
    return axios
      .get(`${config.API_URL}/attendance/${id}/history`)
      .then(resp => resp.data)
      .then(data => {
        dispatch(action(FETCH_ATTENDANCE_HISTORY.SUCCESS, data))
        return data
      })
      .catch(e => dispatch(actionFailed(FETCH_ATTENDANCE_HISTORY, e)))
  }
}

export function changeResponsible(serviceId, userId) {
  return (dispatch, getState) => {
    dispatch(action(CHANGE_RESPONSIBLE.ACTION))
    return axios
      .patch(`${config.API_URL}/attendance/${serviceId}/user`, {
        user_id: userId,
      })
      .then(resp => {
        dispatch(action(CHANGE_RESPONSIBLE.SUCCESS, resp.data))
        const { filters, sizePerPage, page } = getState().attendance
        dispatch(fetchAttendanceInit(filters, sizePerPage, page))
        return resp
      })
      .catch(e => dispatch(actionFailed(CHANGE_RESPONSIBLE, e)))
  }
}

export function getPermission() {
  const permissions = JSON.parse(localStorage.getItem('_higsystem_permissions'))

  if (!permissions) {
    return []
  }

  return permissions
}

export function getNfseCompanies(id) {
  return dispatch => {
    dispatch(action(FETCH_NFSE_COMPANIES.ACTION))
    return axios
      .get(`${config.API_URL}/laundry-nfsen-companies`)
      .then(resp => resp.data)
      .then(data => {
        dispatch(action(FETCH_NFSE_COMPANIES.SUCCESS, data))
        return data
      })
      .catch(e => dispatch(actionFailed(FETCH_NFSE_COMPANIES, e)))
  }
}
